// After a deploy, a page loaded from the previous build asks for code chunks that no longer exist
// (Workers Assets serves one build). Reloading picks up the current build's page and chunks, so a
// missing chunk reloads the page once instead of showing the error page.

const STALE_CHUNK_MESSAGES = [
  "Failed to fetch dynamically imported module", // Chromium
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari
  "Unable to preload CSS", // Vite's CSS preload
];

/** Whether `error` is a code chunk of this page's build failing to load. */
export const isStaleBuildError = (error: unknown): boolean =>
  error instanceof Error && STALE_CHUNK_MESSAGES.some((message) => error.message.includes(message));

const RELOAD_KEY = "calmlens:stale-build-reload";
/** A page that still fails this soon after reloading for it shows the error page instead. */
const RELOAD_GUARD_MS = 60_000;
/**
 * Firefox and Safari fail the loads they cancel while a page unloads, like missing files. Failures
 * this soon after the page started leaving don't reload it: the reload would replace the
 * navigation the visitor asked for. A cancelled unload only pauses recovery this long.
 */
const LEAVING_GRACE_MS = 5_000;
let leftAt = Number.NEGATIVE_INFINITY;

/**
 * Whether this page is reloading onto the current build (marked on the document until it unloads).
 * Errors from the missing code that show up meanwhile (a module that resolved to nothing, a render
 * that failed) are moot, not reported.
 */
export const isReloadingForCurrentBuild = () =>
  document.documentElement.dataset.staleBuildReload === "true";

/**
 * Reloads the page to load the current build, unless it already did for this path within the
 * last minute (a chunk that is missing from the current build too). Returns whether it reloads.
 */
export const reloadForCurrentBuild = (): boolean => {
  if (Date.now() - leftAt < LEAVING_GRACE_MS) return false;
  const path = window.location.pathname;
  try {
    const [lastPath, lastTime] = (sessionStorage.getItem(RELOAD_KEY) ?? "").split("|");
    if (lastPath === path && Date.now() - Number(lastTime) < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, `${path}|${Date.now()}`);
  } catch {
    // Without session storage there is no loop guard, so don't reload.
    return false;
  }
  document.documentElement.dataset.staleBuildReload = "true";
  window.location.reload();
  return true;
};

/**
 * Whether a stylesheet that has finished loading failed: it has no `sheet`, or (Chromium, which
 * gives a failed stylesheet an empty one) a same-origin sheet without rules. No build stylesheet is
 * empty. A cross-origin sheet's rules can't be read, so it counts as loaded.
 */
const isFailedStylesheet = (link: HTMLLinkElement) => {
  if (!link.relList.contains("stylesheet") || link.disabled) return false;
  if (!link.sheet) return true;
  try {
    return link.sheet.cssRules.length === 0;
  } catch {
    return false;
  }
};

/**
 * Reloads onto the current build when one of the page's stylesheets fails to load, which leaves it
 * unstyled (code chunks are handled where their import fails: the error page and error reporting).
 * Call it from the browser entry, which runs after the stylesheets in `<head>` have loaded or failed
 * (a module script waits for them); stylesheets linked later report their failure as an `error`
 * event. Returns a function that stops watching.
 */
export const installStylesheetRecovery = (): (() => void) => {
  const stop = new AbortController();
  window.addEventListener(
    "beforeunload",
    () => {
      leftAt = Date.now();
    },
    { signal: stop.signal },
  );
  const links = [...document.head.querySelectorAll<HTMLLinkElement>("link[href]")];
  if (links.some(isFailedStylesheet) && reloadForCurrentBuild()) return () => stop.abort();
  // Error events don't bubble, so listen in the capture phase.
  document.addEventListener(
    "error",
    (event) => {
      const target = event.target as Element | null;
      if (
        target?.tagName === "LINK" &&
        (target as HTMLLinkElement).relList.contains("stylesheet")
      ) {
        reloadForCurrentBuild();
      }
    },
    { capture: true, signal: stop.signal },
  );
  return () => stop.abort();
};
